/**
 * Pure helpers for the events surface (§ G11).
 *
 * The datetime conversion is here rather than inline because it is the one
 * place this page can be quietly wrong: an organizer types "9:00 AM" meaning
 * the meeting's own clock, and getting the conversion backwards shifts every
 * attendee's arrival verdict by the offset without any visible symptom.
 */

/**
 * `<input type="datetime-local">` yields "2026-09-15T09:00" — a wall-clock time
 * with NO zone. `new Date()` interprets that in the BROWSER's zone.
 *
 * ⚠️ Kept only for callers with nothing better; prefer `zonedInputToIso` (§ G24).
 * Pairing this with `browserTimezone()` looked right and was not: an organizer in
 * Dallas typing "11:00" for a meeting in Atlanta got an instant one hour early
 * AND a stored zone of America/Chicago, so the panel re-rendered it as "11:00 AM"
 * and looked perfect while every arrival deadline was computed off noon Eastern.
 * Found on Wade's live event, 2026-08-17.
 */
export function localInputToIso(value: string): string | null {
  if (!value || !value.trim()) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** The reverse, for populating the input from a stored instant. */
export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function browserTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * The offset, in ms, that `timeZone` was at a given instant (positive east of
 * UTC). Derived from Intl so the platform handles DST, mirroring the backend's
 * `airportTimezone.tzOffsetAt`.
 */
function tzOffsetMs(ms: number, timeZone: string): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(new Date(ms));
    const m: Record<string, string> = {};
    for (const p of parts) m[p.type] = p.value;
    const asUtc = Date.UTC(
      Number(m.year),
      Number(m.month) - 1,
      Number(m.day),
      Number(m.hour) % 24,
      Number(m.minute),
      Number(m.second)
    );
    return asUtc - ms;
  } catch {
    return null;
  }
}

/**
 * A wall clock read in a NAMED zone → a true instant (§ G24).
 *
 * "11:00 on Aug 26, as read in Atlanta" is what an organizer means when they type
 * a meeting time, wherever they happen to be sitting. `localInputToIso` could
 * only ever mean "as read where I am", which is the same string with a different
 * meaning and no visible difference on screen.
 *
 * Falls back to the browser's own zone when no zone is known — an unknown city
 * airport still has to be settable, and the surface says which zone it used
 * rather than pretending.
 */
export function zonedInputToIso(value: string, timeZone: string | null | undefined): string | null {
  if (!value || !value.trim()) return null;
  if (!timeZone) return localInputToIso(value);

  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!m) return localInputToIso(value);
  const asUtc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));

  const first = tzOffsetMs(asUtc, timeZone);
  if (first === null) return localInputToIso(value);
  let t = asUtc - first;
  // Re-read the offset AT the candidate instant: near a DST boundary the offset
  // that applies to the answer is not the one that applied to the guess.
  const second = tzOffsetMs(t, timeZone);
  if (second !== null && second !== first) t = asUtc - second;
  return new Date(t).toISOString();
}

/** The reverse: an instant → the datetime-local string as read in `timeZone`. */
export function isoToZonedInput(iso: string | null | undefined, timeZone: string | null | undefined): string {
  if (!iso) return '';
  if (!timeZone) return isoToLocalInput(iso);
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(d);
    const m: Record<string, string> = {};
    for (const p of parts) m[p.type] = p.value;
    const hour = m.hour === '24' ? '00' : m.hour;
    return `${m.year}-${m.month}-${m.day}T${hour}:${m.minute}`;
  } catch {
    return isoToLocalInput(iso);
  }
}

/** "EDT" / "JST" — the short zone name, for labelling a time input honestly. */
export function zoneAbbrev(timeZone: string | null | undefined, at?: string | null): string {
  if (!timeZone) return '';
  try {
    const when = at ? new Date(at) : new Date(Date.UTC(2026, 0, 15, 12, 0));
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' }).formatToParts(
      Number.isNaN(when.getTime()) ? new Date(Date.UTC(2026, 0, 15, 12, 0)) : when
    );
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/**
 * The warning for a meeting stored in somebody's LAPTOP zone rather than the
 * city's (§ G24).
 *
 * Existing events are not silently rewritten — moving a real meeting by an hour
 * without being asked is worse than displaying a wrong-looking time — so the
 * mismatch is stated, with the correction the organizer can make in one save.
 */
export function zoneMismatchNote(
  storedZone: string | null | undefined,
  cityZone: string | null | undefined,
  airport?: string | null
): string {
  if (!storedZone || !cityZone || storedZone === cityZone) return '';
  const city = airport ? `${airport}'s` : "the city's";
  return `This meeting's time is stored in ${storedZone}, not ${city} clock (${cityZone}). Re-save the time below to fix it — every arrival deadline is computed on the city's clock.`;
}

/**
 * What changing the meeting did to everyone's legs (§ G21).
 *
 * Silent when nothing moved. When something did, it says how many — because
 * changing one field on one panel just rewrote the arrival deadline on other
 * people's itineraries, and an organizer who is not told that will not believe it
 * later. `left_alone` is named too: those are legs somebody had already timed by
 * hand, and knowing they survived is the difference between trusting this button
 * and never pressing it again.
 */
export function retimeNote(retimed: {
  updated: number;
  left_alone: number;
  window: { latest_arrival: string | null; earliest_departure: string | null };
}): string {
  if (!retimed || retimed.updated <= 0) return '';
  const legs = retimed.updated === 1 ? '1 leg' : `${retimed.updated} legs`;
  const kept =
    retimed.left_alone > 0
      ? ` ${retimed.left_alone} you had timed by hand were left alone.`
      : '';
  const to = retimed.window.latest_arrival
    ? ` Now shopping arrivals by ${retimed.window.latest_arrival}${
        retimed.window.earliest_departure ? ` and departures from ${retimed.window.earliest_departure}` : ''
      }.`
    : ' No arrival deadline is claimed now.';
  return `Re-timed ${legs}.${kept}${to}`;
}

/** "3 attendees" / "1 attendee" / "no attendees yet" — never "0 attendees". */
export function eventSubtitle(attendees: number): string {
  if (!Number.isFinite(attendees) || attendees <= 0) return 'no attendees yet';
  return attendees === 1 ? '1 attendee' : `${attendees} attendees`;
}
